import { useAppStore } from '@core/store'
import { whenRecoveryResolved } from '@core/session/recovery'
import { clashKey, type ClashResult } from '@viewer/clash/lib'
import { viewerApi } from '@viewer/lib/viewerApi'
import { AUTOR_SEM_SESSAO, resolveClashGuids } from './clashBcfExport'
import {
  contagensDaCorrida,
  fraseDoResumo,
  migrarPorGuids,
  reconciliar,
  type ClashConfigCorrida,
  type ClashCorrida,
  type ResumoReconciliacao,
} from './clashReconciliacao'
import { temDecisao, type ClashTriageMap } from './clashTriage'
import { ganchoDaPartilha } from './clashPartilhaGancho'

// ---------------------------------------------------------------------------
// Depois de uma corrida: reconciliar a triagem e registar a corrida no
// historico (ver clashReconciliacao.ts para as regras).
//
// A parte que precisa do viewer esta aqui: os GlobalId dos conflitos
// triados que continuam a ocorrer (para a corrida seguinte saber se os
// elementos ainda existem) e a pergunta "este GlobalId ainda existe neste
// modelo?" para os que deixaram de ocorrer.
// ---------------------------------------------------------------------------

/** Onde esta cada GlobalId: o primeiro modelo carregado que o tem.
 *  Uma ida ao Fragments por modelo. Sem resposta de um modelo, o
 *  GlobalId fica "nao se sabe" (conta como existente). */
async function localizarGuids(
  guids: ReadonlySet<string>,
  modelIds: readonly string[],
): Promise<Map<string, string | false | null>> {
  const out = new Map<string, string | false | null>()
  const lista = [...guids]
  if (lista.length === 0) return out
  for (const g of lista) out.set(g, false)
  for (const modelId of modelIds) {
    const model = viewerApi.getModel(modelId)
    if (!model) {
      for (const g of lista) if (out.get(g) === false) out.set(g, null)
      continue
    }
    try {
      const ids = await model.getLocalIdsByGuids(lista)
      lista.forEach((g, i) => {
        if (ids[i] !== null && ids[i] !== undefined && typeof out.get(g) !== 'string') out.set(g, modelId)
      })
    } catch {
      for (const g of lista) if (out.get(g) === false) out.set(g, null)
    }
  }
  return out
}

/**
 * Reconcilia a triagem com a corrida acabada e regista-a. Devolve o
 * resumo, ou null quando nao ha nada a registar (corrida cancelada).
 */
export async function registarCorridaReconciliada(o: {
  resultados: readonly ClashResult[]
  config: ClashConfigCorrida
  cancelada: boolean
  autor: string | null
}): Promise<ResumoReconciliacao | null> {
  // Uma corrida cancelada tem resultados parciais: reconciliar com ela
  // dava por resolvido o que so nao chegou a ser testado.
  if (o.cancelada) return null
  // A triagem tem de estar lida antes de reconciliar. Com a pergunta da
  // recuperacao por responder, as leituras dao vazio: espera-se pela
  // decisao (reconciliar com um mapa vazio apagava a triagem guardada).
  await whenRecoveryResolved()
  useAppStore.getState().hydrateClashTriage()
  if (!useAppStore.getState().clashTriageHydrated) return null
  // Triagem partilhada: o que chegar do servidor durante a reconciliacao
  // espera pelo fim (senao a gravacao da reconciliacao passava-lhe por
  // cima).
  const gancho = ganchoDaPartilha()
  gancho?.inicio()
  try {
    return await reconciliarEGravar(o, gancho)
  } finally {
    gancho?.fim()
  }
}

async function reconciliarEGravar(
  o: { resultados: readonly ClashResult[]; config: ClashConfigCorrida; autor: string | null },
  gancho: ReturnType<typeof ganchoDaPartilha>,
): Promise<ResumoReconciliacao> {
  const presentes = new Set(o.resultados.map(clashKey))

  // GlobalIds de todos os conflitos desta corrida: identificam o mesmo
  // conflito noutra revisao (outros localId, outro nome de ficheiro).
  // Com a triagem partilhada sao sempre precisos: e por eles que os
  // conflitos desta corrida casam com os do servidor.
  const temTriagem = Object.values(useAppStore.getState().clashTriage).some(temDecisao)
  const guids = temTriagem || gancho ? await resolveClashGuids(o.resultados) : {}
  const pares = o.resultados.map((r) => ({
    key: clashKey(r),
    guids: guids[clashKey(r)] ?? ([null, null] as [null, null]),
    kind: r.kind,
  }))
  // A triagem le-se depois de resolver os GlobalIds (uma ida ao modelo),
  // com a partilhada por cima nos conflitos desta corrida.
  const triagemLocal = useAppStore.getState().clashTriage
  const triagemAntes = gancho ? gancho.fundir(triagemLocal, pares) : triagemLocal
  const migracao = migrarPorGuids(triagemAntes, pares)
  const triagem: ClashTriageMap = { ...migracao.triagem }
  for (const r of o.resultados) {
    const k = clashKey(r)
    const e = triagem[k]
    const [a, b] = guids[k] ?? [null, null]
    if (e && temDecisao(e) && a && b) triagem[k] = { ...e, guids: [a, b] }
  }

  // Onde estao os elementos dos triados que deixaram de ocorrer.
  const procurar = new Set<string>()
  for (const [k, e] of Object.entries(triagem)) {
    if (presentes.has(k) || !temDecisao(e) || !e.guids) continue
    for (const g of e.guids) procurar.add(g)
  }
  const latestModels = useAppStore.getState().models
  const onde = await localizarGuids(procurar, latestModels.map((m) => m.id))

  const latest = useAppStore.getState()
  const data = new Date().toISOString()
  const anterior = latest.clashHistorico.at(-1) ?? null
  const saida = reconciliar({
    triagem,
    resultados: o.resultados,
    configAnterior: anterior?.config ?? null,
    configActual: o.config,
    modelosCarregados: new Set(latest.models.map((m) => m.id)),
    ondeEsta: (guid) => (onde.has(guid) ? onde.get(guid)! : null),
    data,
    autor: o.autor?.trim() || AUTOR_SEM_SESSAO,
  })
  const corrida: ClashCorrida = {
    data,
    modelos: latest.models.map((m) => ({ ficheiro: m.fileName, sha256: m.sha256 })),
    config: o.config,
    contagens: contagensDaCorrida(o.resultados, saida.triagem),
    resumo: saida.resumo,
  }
  latest.registarCorrida({
    triagem: saida.triagem,
    corrida,
    chaves: [...presentes],
    resumo: fraseDoResumo(saida.resumo),
    ...(migracao.migradas.size > 0 ? { migradas: Object.fromEntries(migracao.migradas) } : {}),
  })
  return saida.resumo
}
